import i18n from '@/utils/i18n';
import React, { useRef } from 'react';
import {
  Animated,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { ARTICLE_MAP } from './welcome-screen-articles';
import { AUTH_ROUTES } from '@/navigators/routes';
import { AuthStackParams } from '@/navigators/types';
import { createStyles } from '@/contexts/theme-context';
import {
  EXTRA_SMALL_MAX_WIDTH,
  navigationArrowWidth,
  useFooterHeight,
  WelcomePageContainer,
} from '@/components/welcome-page-container';
import { partnerSupplier } from '@/utils/environment';
import { ResizeMode, Video } from 'expo-av';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAuthStackContext } from '@/contexts/auth-stack-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useMediaQuery } from 'react-responsive';
import { WelcomePageButtonConfig } from '@/components/welcome-page-types';

interface Props {
  navigation: StackNavigationProp<AuthStackParams, AUTH_ROUTES.Welcome>;
}

const useWelcomePageButtons = ({
  onSignIn,
  onExploreCatalog,
  onBecomeCustomer,
}: {
  onBecomeCustomer: () => void;
  onExploreCatalog: () => void;
  onSignIn: () => void;
}): WelcomePageButtonConfig[] => {
  const { guestModeEnabled, loginRequestEnabled } = useAuthStackContext();

  const signInButton: WelcomePageButtonConfig = {
    isPrimary: true,
    onPress: onSignIn,
    text: i18n.t('sign_in'),
    trackingName: 'welcome.sign_in_button',
  };

  const exploreCatalogButton: WelcomePageButtonConfig = {
    isPrimary: false,
    onPress: onExploreCatalog,
    text: i18n.t('explore_catalog'),
    trackingName: 'welcome.explore_catalog_button',
  };

  const becomeCustomerButton: WelcomePageButtonConfig = {
    isPrimary: false,
    onPress: onBecomeCustomer,
    text: i18n.t('become_customer'),
    trackingName: 'welcome.become_customer_button',
  };

  if (guestModeEnabled && loginRequestEnabled) {
    return [signInButton, exploreCatalogButton, becomeCustomerButton];
  }

  if (guestModeEnabled) {
    return [exploreCatalogButton, signInButton];
  }

  if (loginRequestEnabled) {
    return [becomeCustomerButton, signInButton];
  }

  return [signInButton];
};

export const WelcomeScreen: React.FC<Props> = ({ navigation }) => {
  const {
    appBackgroundImageUrl,
    appBackgroundVideoUrl,
    cmsArticles,
    navigateToBecomeCustomer,
    tagline,
  } = useAuthStackContext();
  const { isLargeLayout, isSmallLayout } = useLayoutContext();
  const isExtraSmallLayout = useMediaQuery({
    maxWidth: EXTRA_SMALL_MAX_WIDTH,
  });

  const { width } = useWindowDimensions();
  const [styles, theme] = useStyles();

  const opacityValue = useRef(new Animated.Value(0));

  const actionButtons = useWelcomePageButtons({
    onBecomeCustomer: navigateToBecomeCustomer,
    onExploreCatalog: () => navigation.navigate(AUTH_ROUTES.GuestCatalog),
    onSignIn: () => navigation.navigate(AUTH_ROUTES.SignIn),
  });

  const { articleFooterHeight, welcomeFooterHeight } = useFooterHeight(
    actionButtons.length,
  );

  const background = appBackgroundVideoUrl ? (
    <Animated.View
      style={[StyleSheet.absoluteFill, { opacity: opacityValue.current }]}
    >
      <Video
        isLooping
        isMuted
        onLoad={() => {
          Animated.timing(opacityValue.current, {
            toValue: 1,
            useNativeDriver: Platform.OS !== 'web',
          }).start();
        }}
        resizeMode={ResizeMode.COVER}
        shouldPlay
        source={{
          uri: appBackgroundVideoUrl,
        }}
        style={StyleSheet.absoluteFill}
        videoStyle={styles.video}
      />
      <View style={styles.backgroundOverlay} />
    </Animated.View>
  ) : appBackgroundImageUrl ? (
    <View style={StyleSheet.absoluteFill}>
      <Image
        alt=""
        source={{ uri: appBackgroundImageUrl }}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.backgroundOverlay} />
    </View>
  ) : null;

  const articles = [];
  if (partnerSupplier.demoEnabled) {
    const articleKeySet = new Set<string>(cmsArticles);
    for (const [articleKey, article] of Object.entries(ARTICLE_MAP)) {
      if (articleKeySet.has(articleKey)) {
        articles.push(
          <View
            key={article.name}
            style={[
              styles.articleScreenContainer,
              { paddingBottom: articleFooterHeight },
            ]}
          >
            <ScrollView
              contentContainerStyle={{ width }}
              showsVerticalScrollIndicator={false}
            >
              {isLargeLayout ? null : (
                <Image
                  alt=""
                  source={{
                    uri: article.imageURL,
                  }}
                  style={[styles.articleHeaderImage, { width }]}
                />
              )}
              <View
                style={[
                  Platform.OS === 'web'
                    ? { marginHorizontal: navigationArrowWidth }
                    : null,
                  Platform.OS === 'web' && !isLargeLayout
                    ? styles.spaceForNavArrows
                    : null,
                  isLargeLayout ? { marginTop: SCREEN_PADDING } : null,
                  styles.articleContentsContainer,
                ]}
              >
                {article.titleContent}
                {isLargeLayout ? (
                  <Image
                    alt=""
                    source={{
                      uri: article.imageURL,
                    }}
                    style={[
                      styles.articleHeaderImage,
                      styles.articleHeaderImageLarge,
                    ]}
                  />
                ) : null}
                {article.bodyContent}
              </View>
            </ScrollView>
          </View>,
        );
      }
    }
  }

  return (
    <WelcomePageContainer welcomeButtons={actionButtons}>
      <View style={[styles.welcomeScreenContainer, { width }]}>
        <View style={StyleSheet.absoluteFill}>
          {background}
          {Platform.OS === 'web' ? null : (
            <View pointerEvents="none" style={styles.taglineContainer}>
              <Text
                style={[
                  TYPOGRAPHY.h1,
                  styles.tagline,
                  isSmallLayout ? styles.taglineSmallFont : null,
                  isExtraSmallLayout ? styles.taglineExtraSmallFont : null,
                  { marginBottom: welcomeFooterHeight },
                  {
                    color:
                      appBackgroundVideoUrl || appBackgroundImageUrl
                        ? theme.colors.white
                        : theme.colors.primary,
                  },
                ]}
              >
                {tagline}
              </Text>
            </View>
          )}
        </View>
      </View>
      {articles}
    </WelcomePageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  articleContentsContainer: {
    alignSelf: 'center',
    marginHorizontal: SCREEN_PADDING,
    maxWidth: 800 + 2 * SCREEN_PADDING,
  },
  articleHeaderImage: {
    height: 280,
    marginBottom: 16,
    marginTop: 0,
  },
  articleHeaderImageLarge: {
    height: 400,
    width: '100%',
  },
  articleScreenContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    flex: 1,
  },
  backgroundOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.colors.black,
    opacity: 0.45,
  },
  spaceForNavArrows: {
    paddingHorizontal: navigationArrowWidth,
  },
  tagline: {
    fontSize: 46,
    lineHeight: 54,
    maxWidth: 500 + 2 * SCREEN_PADDING,
    paddingHorizontal: SCREEN_PADDING,
    paddingVertical: 12,
    width: '100%',
  },
  taglineContainer: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'flex-end',
    width: '100%',
  },
  taglineExtraSmallFont: {
    fontSize: 30,
    lineHeight: 38,
  },
  taglineSmallFont: {
    fontSize: 38,
    lineHeight: 46,
  },
  video: {
    height: '100%',
    width: '100%',
  },
  welcomeScreenContainer: {
    flexGrow: 1,
  },
}));
